import { CHART_NOTES_LIMIT } from "constants/chart-notes";
import { formatChartNote } from "utils/chart-notes.util";
import { formatValidQueryParamUrl } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  ChartNote,
  ChartNoteBulkPinInput,
  ChartNoteCountsParams,
  ChartNoteCountsResponse,
  ChartNoteFilters,
  ChartNoteInput,
  ChartNotesResponse
} from "./types/chart-notes.type";
import type { PromptOutputFormat } from "./types/prompts.type";

const CHART_NOTE_QUERY =
  "{id,type,text,created_by,updated_by,created_at,updated_at,clinic{id,name,display_name,logo,practice{id,name,logo}}}";

export const chartNotesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getChartNotes: builder.query<ChartNotesResponse, ChartNoteFilters>({
      query: ({
        patient,
        offset = 0,
        limit = CHART_NOTES_LIMIT,
        query = "{}",
        ordering = "-pinned,-display_moment,-id",
        ...filters
      }) =>
        `/chart-notes/?patient=${patient}&limit=${limit}&offset=${offset}&${formatValidQueryParamUrl(
          filters
        )}&ordering=${ordering}&query=${query}`,
      providesTags: (result = []) =>
        result?.map((chartNote) => ({
          type: "ChartNotes" as const,
          id: chartNote.id
        })),
      transformResponse: (response: ChartNotesResponse) =>
        response.map(formatChartNote)
    }),
    getChartNote: builder.query<ChartNote, { noteId: number; query?: string }>({
      query: ({ noteId, query = CHART_NOTE_QUERY }) =>
        `/chart-notes/${noteId}/?query=${query}`,
      providesTags: (chartNote) => [
        {
          type: "ChartNotes" as const,
          id: chartNote?.id
        }
      ],
      transformResponse: formatChartNote
    }),
    getChartNoteCounts: builder.query<
      ChartNoteCountsResponse,
      ChartNoteCountsParams
    >({
      query: ({ patient }) => `/chart-notes/count/?patient=${patient}`,
      providesTags: (_result, _err, arg) => [
        { type: "ChartNoteCounts" as const, id: arg.patient }
      ]
    }),
    addChartNote: builder.mutation<ChartNote, Partial<ChartNoteInput>>({
      query: (noteData) => ({
        url: "/chart-notes/",
        method: "POST",
        body: noteData
      }),
      transformResponse: formatChartNote,
      invalidatesTags: (result, _error, arg) => {
        const tags: Array<
          | "ChartNotes"
          | "Appointments"
          | "ChartNoteCounts"
          | { type: "ChartNoteCounts"; id: number }
        > = ["ChartNotes", "Appointments"];

        if (typeof arg?.patient === "number") {
          tags.push({ type: "ChartNoteCounts", id: arg.patient });
          return tags;
        }

        if (result) {
          const pid =
            typeof result.patient === "number"
              ? result.patient
              : result.patient?.id;
          if (typeof pid === "number") {
            tags.push({ type: "ChartNoteCounts", id: pid });
            return tags;
          }
        }

        tags.push("ChartNoteCounts");
        return tags;
      }
    }),
    updateChartNote: builder.mutation<ChartNote, Partial<ChartNoteInput>>({
      query: ({ id, ...noteData }) => ({
        url: `/chart-notes/${id}/`,
        method: "PATCH",
        body: noteData
      }),
      transformResponse: formatChartNote,
      invalidatesTags: (chartNote) => [
        {
          type: "ChartNotes" as const,
          id: chartNote?.id
        }
      ]
    }),
    bulkPinChartNote: builder.mutation<ChartNote, ChartNoteBulkPinInput>({
      query: (data) => ({
        url: `/chart-notes/bulk-pin/`,
        method: "POST",
        body: {
          pin: data?.pin?.join(","),
          unpin: data?.unpin?.join(",")
        }
      }),
      transformResponse: formatChartNote,
      invalidatesTags: ["ChartNotes"]
    }),
    deleteChartNote: builder.mutation<string, number>({
      query: (id) => ({
        url: `/chart-notes/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["ChartNotes", "ChartNoteCounts"]
    }),

    autoCompleteChartNoteAskAi: builder.mutation<
      string,
      {
        id: number;
        clinic_id?: number;
        text?: string;
        user_prompt?: string;
        as_soap_note?: boolean;
        format?: PromptOutputFormat;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/chart-notes/${id}/complete`,
        method: "POST",
        body
      })
    })
  })
});

export const {
  useGetChartNotesQuery,
  useGetChartNoteQuery,
  useGetChartNoteCountsQuery,
  useAddChartNoteMutation,
  useUpdateChartNoteMutation,
  useBulkPinChartNoteMutation,
  useDeleteChartNoteMutation,
  useAutoCompleteChartNoteAskAiMutation
} = chartNotesApi;
